import { Inject, Injectable } from '@angular/core';
import { from, OperatorFunction } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { INewRelicService } from '../../../new-relic/new-relic-service.interface';
import { NEW_RELIC_SERVICE_TOKEN } from '../../../new-relic/new-relic-token';

@Injectable()
export class HeicConversionService {
    constructor(
        @Inject(NEW_RELIC_SERVICE_TOKEN)
        private _newRelicService: INewRelicService
    ) {}
    convertHeicFiles(): OperatorFunction<File[], File[]> {
        return (source) =>
            source.pipe(mergeMap((files) => from(this.convertAll(files))));
    }

    private async convertAll(files: File[]): Promise<File[]> {
        return Promise.all(files.map((file) => this.tryConvert(file)));
    }

    private async tryConvert(file: File): Promise<File> {
        if (!this.isHeicFile(file)) return file;
        const heicTo = await this.loadHeicTo();
        if (!heicTo) return file;
        return this.convertSingleFile(file, heicTo);
    }

    private async loadHeicTo(): Promise<typeof import('heic-to/csp')['heicTo'] | undefined> {
        try {
            const { heicTo } = await import('heic-to/csp');
            return heicTo;
        } catch (e) {
            this._newRelicService.noticeError(
                e instanceof Error ? e : new Error(String(e)),
                false,
                { heicToFailureStage: 'load' }
            );
            return undefined;
        }
    }

    private async convertSingleFile(
        file: File,
        heicTo: typeof import('heic-to/csp')['heicTo']
    ): Promise<File> {
        try {
            const blob = await heicTo({
                blob: file,
                type: 'image/jpeg',
                quality: 0.9
            });
            const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
            return new File([blob], newName, { type: 'image/jpeg' });
        } catch (e) {
            this._newRelicService.noticeError(
                e instanceof Error ? e : new Error(String(e)),
                false,
                { heicToFailureStage: 'convert' }
            );
            return file;
        }
    }

    private isHeicFile(file: File): boolean {
        const type = file.type.toLowerCase();
        const name = file.name.toLowerCase();
        return (
            type === 'image/heic' ||
            type === 'image/heif' ||
            name.endsWith('.heic') ||
            name.endsWith('.heif')
        );
    }
}
